'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { GridScan } from './GridScan';
import { HOME_CONTENT } from './HomeContent';
import { RichText } from './RichText';

gsap.registerPlugin(ScrollTrigger);

// Config - tune these values based on your connection & asset size
const INITIAL_BURST_FRAMES = 750; // First big fast load
const CONCURRENT_LIMIT = 500; // Aggressive concurrency - most important speedup
const LOOKAHEAD_FRAMES = 900; // Preload around current position
const PRIORITY_THRESHOLD = 1460; // High priority up to this frame

/* ================= PRELOADER CLASS ================= */
class FrameSequencePreloader {
  constructor({
    totalFrames,
    getFramePath,
    onProgress = () => {},
    onEarlyReady = () => {},
    onComplete = () => {},
  }) {
    this.totalFrames = totalFrames;
    this.getFramePath = getFramePath;
    this.images = new Array(totalFrames).fill(null);
    this.loadedSet = new Set();
    this.loadingSet = new Set();
    this.loadedCount = 0;
    this.cancelled = false;
    this.earlyReadyTriggered = false;

    this.onProgress = onProgress;
    this.onEarlyReady = onEarlyReady;
    this.onComplete = onComplete;
  }

  async load() {
    // Phase 1: Fast initial burst
    await this.loadRange(0, INITIAL_BURST_FRAMES, true);

    // Unlock interaction early
    if (!this.earlyReadyTriggered) {
      this.earlyReadyTriggered = true;
      this.onEarlyReady();
    }

    // Phase 2: Continue background loading
    this.loadRemainingInBackground();
  }

  async loadRange(start, count, highPriority = false) {
    const end = Math.min(start + count, this.totalFrames);
    const indices = Array.from({ length: end - start }, (_, i) => start + i);

    // Split into concurrent-safe chunks
    for (let i = 0; i < indices.length; i += CONCURRENT_LIMIT) {
      const chunk = indices.slice(i, i + CONCURRENT_LIMIT);
      const promises = chunk.map((idx) =>
        this.loadSingleImage(idx, highPriority)
      );
      await Promise.allSettled(promises);
    }
  }

  loadSingleImage(index, highPriority = false) {
    return new Promise((resolve) => {
      if (this.cancelled) return resolve();
      if (this.loadedSet.has(index)) return resolve();

      if (this.loadingSet.has(index)) {
        // Already loading → just wait
        const check = () => {
          if (this.loadedSet.has(index)) resolve();
          else setTimeout(check, 30);
        };
        check();
        return;
      }

      this.loadingSet.add(index);

      const img = new Image();
      img.decoding = 'eager';

      // Priority hints
      if (highPriority || index < PRIORITY_THRESHOLD) {
        img.fetchPriority = 'high';
      } else {
        img.fetchPriority = 'low';
      }

      img.src = this.getFramePath(index);

      img.onload = () => {
        if (this.cancelled) return resolve();
        this.images[index] = img;
        this.loadedSet.add(index);
        this.loadingSet.delete(index);
        this.loadedCount++;
        this.onProgress(this.loadedCount, this.totalFrames);

        if (
          this.loadedCount >= INITIAL_BURST_FRAMES &&
          !this.earlyReadyTriggered
        ) {
          this.earlyReadyTriggered = true;
          this.onEarlyReady();
        }
        if (this.loadedCount >= this.totalFrames) {
          this.onComplete();
        }
        resolve();
      };

      img.onerror = () => {
        console.warn(`Frame ${index + 1} failed`);
        this.loadedSet.add(index); // count as "done" even on error
        this.loadingSet.delete(index);
        this.loadedCount++;
        this.onProgress(this.loadedCount, this.totalFrames);
        if (this.loadedCount >= this.totalFrames) this.onComplete();
        resolve();
      };
    });
  }

  // Background loading with look-ahead
  loadRemainingInBackground(currentFrame = 0, range = LOOKAHEAD_FRAMES) {
    if (this.cancelled) return;

    const start = Math.max(0, currentFrame - range / 2);
    const end = Math.min(this.totalFrames, currentFrame + range);

    for (let i = start; i < end; i++) {
      if (!this.loadedSet.has(i) && !this.loadingSet.has(i)) {
        this.loadSingleImage(i, false);
      }
    }
  }

  updateCurrentFrame(frame) {
    this.loadRemainingInBackground(frame);
  }

  getImage(index) {
    return this.images[index] || null;
  }

  getProgress() {
    return Math.min(
      100,
      Math.round((this.loadedCount / this.totalFrames) * 100)
    );
  }

  cancel() {
    this.cancelled = true;
    this.images = [];
  }
}

/* ================= MAIN COMPONENT ================= */
const CanvasSequenceHero = () => {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);

  const preloaderRef = useRef(null);
  const rafRef = useRef(null);
  const idleAnimationRef = useRef(null);
  const contentRefs = useRef({});

  const [isOutro, setIsOutro] = useState(false);
  const [frame, setFrame] = useState(0);
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const frameRef = useRef(0);

  const isMobile = useMemo(() => {
    return (
      typeof window !== 'undefined' &&
      (window.matchMedia('(max-width: 768px)').matches ||
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent))
    );
  }, []);

  const TOTAL_FRAMES = isMobile ? 1460 : 1435;
  const SCROLL_MULTIPLIER = isMobile ? 10 : 16;
  const IDLE_FRAMES = 50;
  const IDLE_FPS = 30;
  const OUTRO_SCROLL = 200;

  const FRAME_PATH = (i) =>
    isMobile
      ? `assets/images/img/updated_seq/mobile_v2/webp/frame${String(i + 1).padStart(4, '0')}.webp`
      : `assets/images/img/updated_seq/web_v2/webp/frame${String(i + 1).padStart(4, '0')}.webp`;

  // Skip button handler
  const handleSkip = () => {
    if (!sectionRef.current) return;

    const totalScrollDistance = TOTAL_FRAMES * SCROLL_MULTIPLIER + OUTRO_SCROLL;
    const sectionTop = sectionRef.current.offsetTop;
    const targetScroll = sectionTop + totalScrollDistance;

    window.scrollTo({
      top: targetScroll,
      behavior: 'smooth',
    });
  };

  // Canvas setup & resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctxRef.current = ctx;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const vw = window.visualViewport?.width || window.innerWidth;
      const vh = window.visualViewport?.height || window.innerHeight;

      canvas.width = vw * dpr;
      canvas.height = vh * dpr;
      canvas.style.width = `${vw}px`;
      canvas.style.height = `${vh}px`;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      draw(frameRef.current);
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  // Preloading
  useEffect(() => {
    preloaderRef.current = new FrameSequencePreloader({
      totalFrames: TOTAL_FRAMES,
      getFramePath: FRAME_PATH,
      onProgress: (count, total) => {
        setProgress(Math.round((count / total) * 100));
      },
      onEarlyReady: () => {
        setReady(true);
        console.log(`Early ready after ~${INITIAL_BURST_FRAMES} frames`);
      },
      onComplete: () => {
        console.log('Full sequence loaded');
      },
    });

    preloaderRef.current.load();

    return () => {
      preloaderRef.current?.cancel();
      preloaderRef.current = null;
    };
  }, [isMobile]);

  const findNearestLoadedFrame = (target) => {
    const preloader = preloaderRef.current;
    if (!preloader) return null;

    if (preloader.getImage(target)) return target;

    // search backward first (feels natural on scroll)
    for (let i = target - 1; i >= 0; i--) {
      if (preloader.getImage(i)) return i;
    }

    // fallback forward
    for (let i = target + 1; i < TOTAL_FRAMES; i++) {
      if (preloader.getImage(i)) return i;
    }

    return null;
  };

  // Draw function
  const draw = (index) => {
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;

    const img = preloaderRef.current?.getImage(index);
    if (!img || !img.complete) return;

    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;

    ctx.clearRect(0, 0, cw, ch);

    const ratio = img.width / img.height;
    const viewRatio = cw / ch;

    let dw,
      dh,
      dx = 0,
      dy = 0;

    if (ratio > viewRatio) {
      dh = ch;
      dw = dh * ratio;
      dx = (cw - dw) / 2;
    } else {
      dw = cw;
      dh = dw / ratio;
      dy = (ch - dh) / 2;
    }

    ctx.drawImage(img, dx, dy, dw, dh);
  };

  // Scroll + idle animation
  useEffect(() => {
    if (!ready) return;

    let currentIdleFrame = 0;
    let direction = 1;
    let lastIdleTime = 0;

    const startIdle = () => {
      if (idleAnimationRef.current) return;

      const animate = (time) => {
        if (!lastIdleTime) lastIdleTime = time;

        if (time - lastIdleTime >= 1000 / IDLE_FPS) {
          currentIdleFrame += direction;

          if (currentIdleFrame >= IDLE_FRAMES - 1) {
            currentIdleFrame = IDLE_FRAMES - 1;
            direction = -1;
          } else if (currentIdleFrame <= 0) {
            currentIdleFrame = 0;
            direction = 1;
          }

          draw(currentIdleFrame);
          setFrame(currentIdleFrame);
          frameRef.current = currentIdleFrame;
          lastIdleTime = time;
        }

        idleAnimationRef.current = requestAnimationFrame(animate);
      };

      idleAnimationRef.current = requestAnimationFrame(animate);
    };

    const stopIdle = () => {
      if (idleAnimationRef.current) {
        cancelAnimationFrame(idleAnimationRef.current);
        idleAnimationRef.current = null;
      }
    };

    const trigger = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: 'top top',
      end: `+=${TOTAL_FRAMES * SCROLL_MULTIPLIER + OUTRO_SCROLL}`,
      scrub: 0.2,
      pin: true,
      pinSpacing: true,
      invalidateOnRefresh: true,
      refreshPriority: 100,
      onUpdate: (self) => {
        const totalScrollFrames = TOTAL_FRAMES * SCROLL_MULTIPLIER;
        const scrollPos = self.progress * (totalScrollFrames + OUTRO_SCROLL);

        if (scrollPos >= totalScrollFrames) {
          setIsOutro(true);
          const last = TOTAL_FRAMES - 1;
          frameRef.current = last;
          setFrame(last);
          draw(last);
          stopIdle();
          return;
        }

        setIsOutro(false);

        const frameProgress = scrollPos / totalScrollFrames;
        const targetFrame = Math.min(
          TOTAL_FRAMES - 1,
          Math.max(0, Math.floor(frameProgress * TOTAL_FRAMES))
        );

        // Update preloader lookahead
        const velocity = Math.abs(self.getVelocity());

        const dynamicLookahead =
          velocity > 2000 ? 1200 : velocity > 1000 ? 900 : LOOKAHEAD_FRAMES;

        preloaderRef.current?.loadRemainingInBackground(
          targetFrame,
          dynamicLookahead
        );

        if (targetFrame === 0) {
          if (!idleAnimationRef.current) startIdle();
          return;
        }

        stopIdle();

        const safeFrame = findNearestLoadedFrame(targetFrame);
        if (safeFrame === null) return;

        if (safeFrame !== frameRef.current) {
          frameRef.current = safeFrame;
          setFrame(safeFrame);

          if (rafRef.current) cancelAnimationFrame(rafRef.current);
          rafRef.current = requestAnimationFrame(() => draw(safeFrame));
        }
      },
    });

    startIdle();

    return () => {
      trigger?.kill();
      stopIdle();
    };
  }, [ready]);

  const animateTextIn = (container) => {
    const lines = container.querySelectorAll('.text-line');

    gsap.killTweensOf(lines);

    gsap.fromTo(
      lines,
      {
        opacity: 0,
        y: 24,
        filter: 'blur(14px)',
      },
      {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.12,
        overwrite: true,
      }
    );
  };

  const animateTextOut = (container) => {
    const lines = container.querySelectorAll('.text-line');

    gsap.to(lines, {
      opacity: 0,
      y: -10,
      filter: 'blur(10px)',
      duration: 0.35,
      ease: 'power2.in',
      stagger: 0.05,
      overwrite: true,
    });
  };

  useEffect(() => {
    FINAL_HOME_CONTENT.forEach((b) => {
      const el = contentRefs.current[b.id];
      if (!el) return;

      const isActive = frame >= b.from && frame <= b.to;

      if (isActive && !el.dataset.active) {
        el.dataset.active = 'true';

        gsap.to(el, {
          autoAlpha: 1,
          duration: 0.25,
          ease: 'power2.out',
        });

        animateTextIn(el);
      }

      if (!isActive && el.dataset.active) {
        el.dataset.active = '';

        animateTextOut(el);

        gsap.to(el, {
          autoAlpha: 0,
          duration: 0.25,
          ease: 'power2.in',
        });
      }
    });
  }, [frame]);

  // Initial draw
  useEffect(() => {
    if (ready) draw(0);
  }, [ready]);

  const MOBILE_FRAME_OVERRIDES = {
    1: { from: 0, to: 60 },
    2: { from: 70, to: 160 },
    3: { from: 340, to: 370 },
    4: { from: 390, to: 520 },
    5: { from: 440, to: 520 },
    6: { from: 550, to: 900 },
    7: { from: 820, to: 900 },
    8: { from: 850, to: 900 },
    9: { from: 1150, to: 1220 },
    10: { from: 1300, to: 1400 },
  };

  const FINAL_HOME_CONTENT = useMemo(() => {
    if (!isMobile) return HOME_CONTENT;

    return HOME_CONTENT.map((block) => {
      const mobileOverride = MOBILE_FRAME_OVERRIDES[block.id];

      if (!mobileOverride) return block;

      return {
        ...block,
        from: mobileOverride.from,
        to: mobileOverride.to,
      };
    });
  }, [isMobile]);

  return (
    <section ref={sectionRef} className="relative w-full h-screen bg-[#230a03]">
      {!ready && (
        <div className="fixed inset-0 z-[9999] bg-black overflow-hidden">
          <GridScan
            sensitivity={0.55}
            lineThickness={1}
            linesColor="#392e4e"
            gridScale={0.1}
            scanColor="#D51211"
            scanOpacity={0.4}
            enablePost
            bloomIntensity={0.6}
            chromaticAberration={0.002}
            noiseIntensity={0.01}
          />

          <div className="absolute inset-0 flex flex-col items-center justify-center z-50">
            <div className="grid grid-cols-3 gap-2 mb-8 text-sm">
              {Array.from({ length: 9 }).map((_, i) => (
                <span
                  key={i}
                  className="w-3 h-3 bg-primary/50 animate-grid"
                  style={{ animationDelay: `${i * 0.12}s` }}
                />
              ))}
            </div>

            <p className="text-white text-3xl font-bold">{progress}%</p>
            <p className="text-white/60 text-xs mt-3 uppercase tracking-wider">
              Mapping Spatial Grid
            </p>
          </div>
        </div>
      )}

      {/* SKIP BUTTON */}
      {ready && !isOutro && (
        <button
          onClick={handleSkip}
          className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-2 text-white flex items-center justify-center font-telegraf gap-2 bg-white bg-clip-padding backdrop-filter border border-white/30  backdrop-blur bg-opacity-10 backdrop-saturate-100 backdrop-contrast-100 rounded-xl"
        >
          <div className="relative">
            <div
              className="w-2 h-2 bg-white rounded-full"
              style={{
                animation: 'pulse-circle 2s ease-in-out infinite',
              }}
            />
          </div>
          Skip Intro
        </button>
      )}

      <canvas
        ref={canvasRef}
        className="absolute top-1/2  w-screen h-screen block z-0"
      />

      <div className="pointer-events-none absolute inset-0 z-10">
        {FINAL_HOME_CONTENT.map((b) => (
          <div
            key={b.id}
            ref={(el) => {
              if (el) contentRefs.current[b.id] = el;
            }}
            className={`absolute flex flex-col opacity-0 w-full px-2 ${b.customStyle || ''} ${b.position || ''}`}
          >
            {b.lines && (
              <h1
                className={`text-white  font-telegraf font-extrabold ${b.headingStyle || ''}`}
              >
                <RichText lines={b.lines} />
              </h1>
            )}

            {b.desc && (
              <p className={`py-4 ${b.descStyle || ''}`}>
                <RichText
                  lines={b.desc}
                  highlightClass="text-primary font-semibold"
                />
              </p>
            )}
          </div>
        ))}
      </div>

      {/* OUTRO */}
      <div
        className={`absolute inset-0 bg-black z-30 flex items-center justify-center transition-opacity duration-500 ${
          isOutro ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <h1
          className={` text-white font-bold font-telegraf text-4xl md:text-5xl text-center max-w-xl px-4 `}
        >
          Spatial Grid – A Boundless Frontier for{' '}
          <span className="text-primary font-telegraf">
            Immersive Spatial Models.
          </span>
        </h1>
      </div>

      {/* Debug (optional - remove in production) */}
      {/* <div className="absolute bottom-6 left-6 text-white text-sm opacity-60 z-20">
        Frame: {frame} {idleAnimationRef.current ? '(idle)' : ''}
      </div> */}

      {/* <div className="fixed bottom-2 right-2 text-xs text-white bg-black/60 p-2 z-50">
        Loaded: {preloaderRef.current?.loadedCount} / {TOTAL_FRAMES}
      </div> */}

      <style>{`
            @keyframes pulse-circle {
              0%, 100% {
                transform: scale(1);
                opacity: 1;
              }
              50% {
                transform: scale(0.6);
                opacity: 0.7;
              }
            }
          `}</style>
    </section>
  );
};

export default CanvasSequenceHero;
